import AwesomeDebouncePromise from "awesome-debounce-promise";
import classNames from "classnames";
import { fetchAuBusinessSearch } from "Hooks/Api/apiBusinessNumber";
import { EntityControllerData } from "Hooks/Api/shared";
import { useCallback, useEffect, useState } from "react";
import { validateAbnFormat, validateAcnFormat } from "Util/BusinessNumberValidationFormat";
import EntityList, { IEntityListHeaderProps } from "Views/Components/EntityList/EntityList";
import TablePagination from "Views/Components/Pagination/TablePagination";
import InlineSpinner from "Views/Components/Spinner/InlineSpinner";
import { TextFieldSetter } from "Views/Components/TextBox/TextFieldSetter";
import { AustralianEntitySearchBoxSegment } from "./AustralianEntitySearchBoxSegment";
const DEFAULT_PAGE_SIZE = 5;

export type AbrOrAsicSearchResponse = {
	abn: string;
	acn: string;
	identifierNumber: string;
	entityName: string;
	entityStatus: string;
	address: string;
	lookUpMethod: "ABN" | "ACN";
}

async function GetEntitiesFromAbrOrAsicSearch(
	value: string,
	pageNo: number,
	pageSize: number,
	debounceKey?: string
): Promise<EntityControllerData<AbrOrAsicSearchResponse>> {
	let searchTerm = value.trim();
	if (searchTerm.length === 0) {
		return {
			count: 0,
			data: []
		};
	}

	// determine if the search term is an ABN or ACN
	const isValidABN = validateAbnFormat(searchTerm);
	const { isValid: isValidACN, value: parsedAcn } = validateAcnFormat(searchTerm, true);
	if (isValidACN) {
		searchTerm = parsedAcn ?? searchTerm;
	}
	const lookupMethod = isValidABN ? "ABN" : isValidACN ? "ACN" : null;


	const response = await fetchAuBusinessSearch({
		searchTerm: searchTerm,
		lookupMethod: lookupMethod,
		pageNo: pageNo,
		pageSize: pageSize
	});

	return response.data;
}

const debouncedGetEntitiesFromAbrOrAsicSearch = AwesomeDebouncePromise(
	GetEntitiesFromAbrOrAsicSearch,
	900,
	{ leading: true, key: (value, pageNo, pageSize, debounceKey) => debounceKey }
);

function getTableColumns(): IEntityListHeaderProps<AbrOrAsicSearchResponse>[] {
	return [
		{
			displayName: "Name",
			columnName: "EntityName",
			value: entity => entity.entityName,
			showNA: true
		},
		{
			displayName: "ABN",
			columnName: "abn",
			value: entity => entity.abn,
			showNA: true
		},
		{
			displayName: "ACN",
			columnName: "acn",
			value: entity => entity.acn,
			showNA: true
		},
		{
			displayName: "Status",
			columnName: "entityStatus",
			value: entity => entity.entityStatus,
			showNA: true
		},
		{
			displayName: "State",
			columnName: "address",
			value: entity => entity.address,
			showNA: true
		},
	]
}

type Props = {
	debounceKey?: string;
	lookupDetail?: {
		lookupMethod: string;
		lookupValue: string;
	};
	updateDetail: (
		newValue: { lookupMethod: "ABN" | "ACN"; lookupValue: string },
		isValid: boolean,
		entityDetail: AbrOrAsicSearchResponse,
	) => void;
	entityDetail?: AbrOrAsicSearchResponse;
	title?: string;
	errors?: string;
	isDisabled?: boolean;
}

export const AustralianEntitySearchBox: React.FunctionComponent<Props> = ({
	debounceKey,
	lookupDetail,
	updateDetail,
	entityDetail,
	title,
	errors,
	isDisabled = false,
}) => {
	const [displaySearchResults, setDisplaySearchResults] = useState<boolean>(false);
	const [searchTerm, setSearchTerm] = useState<string>(lookupDetail?.lookupValue ?? "");
	const [totalCount, setTotalCount] = useState(0);
	const [pageNo, setPageNo] = useState(1);
	const [availableEntities, setAvailableEntities] = useState<AbrOrAsicSearchResponse[]>([]);
	const [selectedEntity, setSelectedEntity] = useState<AbrOrAsicSearchResponse | undefined>(entityDetail);
	const [loading, setLoading] = useState(false);

	const fetchEntities = useCallback(async (value: string, newPageNo: number = 1) => {
		setLoading(true);
		const result = await debouncedGetEntitiesFromAbrOrAsicSearch(value, newPageNo, DEFAULT_PAGE_SIZE, debounceKey);
		setAvailableEntities(result.data);
		setTotalCount(result.count);

		if (result.data.length === 1) setSelectedEntity(result.data[0]);
		setLoading(false);
	}, []);

	const handleSearchChange = useCallback(async (newValue: string) => {
		setSearchTerm(newValue);
		setDisplaySearchResults(true);
		setPageNo(1);
		await fetchEntities(newValue, 1);
	}, [fetchEntities]);

	const handleEntitySelect = useCallback((entity: AbrOrAsicSearchResponse) => {
		setSelectedEntity(entity);
		setSearchTerm("");
	}, []);

	useEffect(() => {
		if (searchTerm.trim()) fetchEntities(searchTerm, pageNo);
	}, [pageNo, fetchEntities, searchTerm]);

	// Update the detail when selectedEntity is updated and it is valid
	useEffect(() => {
		setDisplaySearchResults(false);
		if (selectedEntity && selectedEntity !== entityDetail) {
			const parsedValue = selectedEntity.identifierNumber.replace(/[^0-9]/g, '');
			const isValidABN = validateAbnFormat(parsedValue);
			updateDetail(
				{
					lookupMethod: isValidABN ? "ABN" : "ACN",
					lookupValue: parsedValue
				},
				true,
				selectedEntity,
			);
		}
	}, [selectedEntity, updateDetail, entityDetail]);

	return (
		<div className="australian-entity-search-box">
			<div className="search-entity search">
				<TextFieldSetter
					className={classNames('search-input')}
					value={searchTerm}
					setValue={setSearchTerm}
					onAfterChange={e => handleSearchChange(e.currentTarget.value)}
					label="Search business/entity"
					placeholder="Enter name, ACN or ABN"
					errors={errors}
					isDisabled={isDisabled}
				/>
			</div>

			{loading && <InlineSpinner />}

			{displaySearchResults && !loading && (
				<>
					{searchTerm.trim().length > 0 && totalCount === 0 && <div className="alert alert-warning">No Business Entity found</div>}
					{totalCount > 1 && (
						<div className="collection-component">
							<EntityList
								collection={availableEntities}
								columns={getTableColumns()}
								idColumn="entityName"
								sortColumn="EntityName"
								sortDescending
								onClickRow={handleEntitySelect}
							/>
							<TablePagination
								perPage={DEFAULT_PAGE_SIZE}
								pageNo={pageNo - 1}
								totalRecords={totalCount}
								onPageChange={newPageNo => {
									setPageNo(newPageNo + 1);
									setSelectedEntity(undefined);
								}}
							/>
						</div>
					)}
				</>
			)}
			{selectedEntity && (
				<AustralianEntitySearchBoxSegment
					entityName={selectedEntity.entityName}
					entityStatus={selectedEntity.entityStatus}
					entityAddress={selectedEntity.address}
					lookupValue={selectedEntity.identifierNumber}
					title={title}
				/>
			)}
		</div>
	)
}